/**
 * Filling a form in from its own link, and leaving questions out of it.
 *
 * `?prefill_<question>=<answer>` starts a question filled in, and
 * `?hide_<question>` leaves it out of the page altogether -- which is how one
 * form serves several audiences: a link sent to a supplier already names the
 * supplier and does not ask.
 *
 * Pure, and small for a reason. The form holds every answer as a *draft
 * string* and hands it to the field's own `fromDraft`, and a url parameter is
 * already a string -- so for thirteen of the fourteen field types that can be
 * prefilled there is nothing to parse here at all: the parameter is the draft.
 * Upstream needs a `parseQueryParameter` per field type because its form binds
 * to values rather than to drafts, and has nowhere else to put the conversion.
 *
 * The exception is a link row, whose draft names rows by id and whose parameter
 * names them by their text -- so that one needs a lookup, which is not
 * something a pure function can do. `linkRowNames` is what it takes instead.
 */
import type { Field } from '@/api/types'
import type { FormField } from './queries'

/**
 * The prefilled answers, keyed by the question they are for.
 *
 * The key is lower-cased and `+` reads as a space, so `?prefill_Full+Name=`
 * finds a question called "full name". A parameter given more than once keeps
 * the last, which is what appending to a link somebody already has means.
 */
export function prefillsFrom(search: Record<string, string>): Map<string, string> {
  return keyedBy(search, 'prefill_')
}

/** The questions the link asks to be left out, by the same naming rules. */
export function hiddenFrom(search: Record<string, string>): Set<string> {
  return new Set(keyedBy(search, 'hide_').keys())
}

function keyedBy(search: Record<string, string>, prefix: string): Map<string, string> {
  const found = new Map<string, string>()
  for (const [key, value] of Object.entries(search)) {
    if (!key.startsWith(prefix)) continue
    found.set(normalise(key.slice(prefix.length)), value)
  }
  return found
}

const normalise = (name: string): string => name.replaceAll('+', ' ').toLowerCase()

/**
 * The three names a question answers to: its label on the form, the column
 * behind it, and `field_<id>`.
 *
 * The label first, because that is what somebody reading the form sees and the
 * only one they can be sure of -- a form may rename a question without
 * renaming the column. The id last, because it is the one name that cannot
 * change and the one nobody would guess.
 */
function namesOf(entry: FormField): string[] {
  return [
    normalise(entry.name),
    normalise(entry.field.name),
    `field_${String(entry.field.id)}`,
  ]
}

/** What a question starts filled in with, or nothing when the link says nothing. */
export function prefillFor(
  entry: FormField,
  prefills: ReadonlyMap<string, string>,
): string | undefined {
  for (const name of namesOf(entry)) {
    const answer = prefills.get(name)
    if (answer !== undefined) return answer
  }
  return undefined
}

/**
 * Whether the link asks for this question to be left out.
 *
 * Matched by the same three names, case-insensitively. Upstream matches only
 * the form's label and only exactly, which makes `?hide_name` and
 * `?prefill_name` disagree about what "name" means on the same link; being the
 * more forgiving of the two cannot break a link that already works.
 */
export function isHidden(entry: FormField, hidden: ReadonlySet<string>): boolean {
  return namesOf(entry).some((name) => hidden.has(name))
}

/**
 * The rows a link row question is prefilled with, by their text.
 *
 * Comma-separated, and a name holding a comma may be wrapped in double quotes
 * -- the same rule a spreadsheet uses, because that is where a list of names
 * usually comes from. A doubled quote inside a quoted name is one quote.
 */
export function linkRowNames(prefill: string): string[] {
  const names: string[] = []
  let current = ''
  let quoted = false

  for (let at = 0; at < prefill.length; at += 1) {
    const character = prefill[at]
    if (quoted) {
      if (character !== '"') current += character
      else if (prefill[at + 1] === '"') {
        current += '"'
        at += 1
      } else quoted = false
      continue
    }
    if (character === '"') quoted = true
    else if (character === ',') {
      names.push(current.trim())
      current = ''
    } else current += character
  }
  names.push(current.trim())

  return names.filter((name) => name !== '')
}

/** Whether a field is one whose prefill has to be looked up rather than read. */
export const needsLookup = (field: Field): boolean => field.type === 'link_row'
